import React, { useRef, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Button, DatePicker, Flex, Popover } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { useDevice } from "hooks/useDevice"
import FiltersSheet from "./FiltersSheet"
import { MAX_SEARCH_GUESTS, MIN_SEARCH_GUESTS, SEARCH_DATE_DISPLAY_FORMAT } from "../constants"
import styles from "./WidgetListing.module.css"

const disablePastDates = (current) => current && current < dayjs().startOf("day")

// "When" + "Guests" + Search. Fields are edited locally and only applied on Search; clearing a
// field (×) applies straight away so the list never shows results for a value no longer on screen.
// Searching is blocked while a request is in flight: the store appends each response, so two
// overlapping searches would merge their results. On a phone both fields open the Filters sheet
// instead (no ×: it has Clear all), which applies its values itself.
const SearchBar = ({ guideUuid, date, guests, isSearching, onSearch }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const containerRef = useRef(null)
  const [draftDate, setDraftDate] = useState(date)
  const [draftGuests, setDraftGuests] = useState(guests)
  const [isGuestsOpen, setIsGuestsOpen] = useState(false)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  // Counter value while the popover is open; committed to draftGuests on Done.
  const [counter, setCounter] = useState(guests || MIN_SEARCH_GUESTS)

  const handleApplyFilters = (filters) => {
    setDraftDate(filters.date)
    setDraftGuests(filters.guests)
    setIsFiltersOpen(false)
    onSearch(filters)
  }

  const guestsText = draftGuests ? t("booking_widget.search.guests_value", { count: draftGuests }) : t("booking_widget.search.guests")

  if (isMobile) {
    return (
      <>
        <Flex align="center" gap={12} className={styles.searchBar}>
          <Flex align="center" className={styles.searchFields}>
            <Flex align="center" className={styles.searchField}>
              <button type="button" className={`${styles.guestsTrigger} ${draftDate ? "" : styles.placeholder}`} onClick={() => setIsFiltersOpen(true)}>
                {draftDate ? draftDate.format(SEARCH_DATE_DISPLAY_FORMAT) : t("booking_widget.search.when")}
              </button>
            </Flex>
            <Flex align="center" className={styles.searchField}>
              <button type="button" className={`${styles.guestsTrigger} ${draftGuests ? "" : styles.placeholder}`} onClick={() => setIsFiltersOpen(true)}>
                {guestsText}
              </button>
            </Flex>
          </Flex>
          <Button type="primary" shape="round" className={styles.searchButton} disabled={isSearching} onClick={() => onSearch({ date: draftDate, guests: draftGuests })}>
            {t("booking_widget.search.search")}
          </Button>
        </Flex>
        <FiltersSheet
          guideUuid={guideUuid}
          isOpen={isFiltersOpen}
          isSearching={isSearching}
          date={draftDate}
          guests={draftGuests}
          onClose={() => setIsFiltersOpen(false)}
          onApply={handleApplyFilters}
        />
      </>
    )
  }

  const handleGuestsOpenChange = (isOpen) => {
    if (isOpen) setCounter(draftGuests || MIN_SEARCH_GUESTS)
    setIsGuestsOpen(isOpen)
  }

  const handleGuestsDone = () => {
    setDraftGuests(counter)
    setIsGuestsOpen(false)
  }

  const handleClearDate = () => {
    setDraftDate(null)
    onSearch({ date: null, guests: draftGuests })
  }

  const handleClearGuests = () => {
    setDraftGuests(null)
    onSearch({ date: draftDate, guests: null })
  }

  const guestsContent = (
    <Flex vertical gap={16} className={styles.guestsPopover}>
      <Flex align="center" justify="space-between" gap={24}>
        <span className={styles.guestsLabel}>{t("booking_widget.search.number_of_guests")}</span>
        <Flex align="center" gap={12}>
          <Button
            shape="circle"
            className={styles.counterButton}
            aria-label={t("booking_widget.search.decrease_guests")}
            disabled={counter <= MIN_SEARCH_GUESTS}
            onClick={() => setCounter((value) => Math.max(MIN_SEARCH_GUESTS, value - 1))}
            icon={<Icon type="feather" name="Minus" width={16} height={16} color="currentColor" />}
          />
          <span className={styles.counterValue}>{counter}</span>
          <Button
            shape="circle"
            className={styles.counterButton}
            aria-label={t("booking_widget.search.increase_guests")}
            disabled={counter >= MAX_SEARCH_GUESTS}
            onClick={() => setCounter((value) => Math.min(MAX_SEARCH_GUESTS, value + 1))}
            icon={<Icon type="feather" name="Plus" width={16} height={16} color="currentColor" />}
          />
        </Flex>
      </Flex>
      <Flex justify="flex-end">
        <Button type="primary" shape="round" onClick={handleGuestsDone}>
          {t("booking_widget.search.done")}
        </Button>
      </Flex>
    </Flex>
  )

  return (
    <Flex align="center" gap={12} className={styles.searchBar} ref={containerRef}>
      <Icon type="feather" name="Search" width={20} height={20} className={styles.searchIcon} />
      <Flex align="center" className={styles.searchFields}>
        <Flex align="center" className={styles.searchField}>
          <DatePicker
            value={draftDate}
            onChange={setDraftDate}
            disabledDate={disablePastDates}
            format={SEARCH_DATE_DISPLAY_FORMAT}
            placeholder={t("booking_widget.search.when")}
            variant="borderless"
            suffixIcon={null}
            allowClear={false}
            inputReadOnly
            getPopupContainer={() => containerRef.current || document.body}
            className={styles.dateInput}
          />
          {draftDate && (
            <Button
              type="text"
              size="small"
              className={styles.clearButton}
              aria-label={t("booking_widget.search.clear_date")}
              disabled={isSearching}
              onClick={handleClearDate}
              icon={<Icon type="feather" name="X" width={16} height={16} color="currentColor" />}
            />
          )}
        </Flex>
        <Flex align="center" className={styles.searchField}>
          <Popover
            open={isGuestsOpen}
            onOpenChange={handleGuestsOpenChange}
            trigger="click"
            placement="bottomLeft"
            arrow={false}
            content={guestsContent}
            getPopupContainer={() => containerRef.current || document.body}
          >
            <button type="button" className={`${styles.guestsTrigger} ${draftGuests ? "" : styles.placeholder}`}>
              {guestsText}
            </button>
          </Popover>
          {draftGuests && (
            <Button
              type="text"
              size="small"
              className={styles.clearButton}
              aria-label={t("booking_widget.search.clear_guests")}
              disabled={isSearching}
              onClick={handleClearGuests}
              icon={<Icon type="feather" name="X" width={16} height={16} color="currentColor" />}
            />
          )}
        </Flex>
      </Flex>
      <Button type="primary" shape="round" className={styles.searchButton} disabled={isSearching} onClick={() => onSearch({ date: draftDate, guests: draftGuests })}>
        {t("booking_widget.search.search")}
      </Button>
    </Flex>
  )
}

SearchBar.propTypes = {
  guideUuid: PropTypes.string.isRequired,
  date: PropTypes.object,
  guests: PropTypes.number,
  isSearching: PropTypes.bool,
  onSearch: PropTypes.func.isRequired
}

SearchBar.defaultProps = {
  date: null,
  guests: null,
  isSearching: false
}

export default SearchBar
